import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdCloudUpload,
  MdDelete,
  MdEdit,
  MdLock,
  MdLockOpen,
  MdFormatListNumbered,
} from 'react-icons/md';
import { ColorPickerPopover } from './ColorPickerPopover';
import { EditZoneModal } from './EditZoneModal';

export function Sidebar({
  plan,
  selectedRowIndex = null,
  selectedSeatGuids = [],
  selectedAreaIndices = [],
  onOpenAutoLabel,
  onAssignCategory,
  onBatchUpdateSeats,
  onDeleteSelectedSeats,
  onUpdateRowProperty,
  onUpdateSingleSeatProperty,
  onUpdateArea,
  onDeleteArea,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onAddZone,
  onEditZone,
  onMoveZoneUp,
  onMoveZoneDown,
  onToggleLockZone,
  onDeleteZone,
  activeZoneIndex = 0,
  setActiveZoneIndex,
  onUpdateBackground,
  onUpdatePlanDimensions,
  onUpdatePlanName,
  onReorderAreaLayer,
  onSelectRow,
  onAddSeatToRow,
  onRemoveSeatFromRow,
}) {
  const bg = useColorModeValue('#FFFFFF', '#1E293B');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const sectionHeaderColor = useColorModeValue('gray.800', 'white');
  const textColorSecondary = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('gray.50', 'navy.900');
  const itemHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const activeZoneBg = useColorModeValue('#EDE9FE', 'rgba(124, 58, 237, 0.25)');
  const curveBoxBg = useColorModeValue('white', 'navy.800');

  // Modals for adding zone & category & editing zone
  const {
    isOpen: isAddZoneOpen,
    onOpen: onOpenAddZone,
    onClose: onCloseAddZone,
  } = useDisclosure();
  const {
    isOpen: isEditZoneOpen,
    onOpen: onOpenEditZone,
    onClose: onCloseEditZone,
  } = useDisclosure();
  const {
    isOpen: isCatModalOpen,
    onOpen: onOpenCatModal,
    onClose: onCloseCatModal,
  } = useDisclosure();

  const [newZoneName, setNewZoneName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#E03400');

  // Editing existing category
  const [editingCatIndex, setEditingCatIndex] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [editCatColor, setEditCatColor] = useState('');

  const categories = plan?.categories ?? [];
  const zones = plan?.zones ?? [];
  const currentZone = zones[activeZoneIndex] ??
    zones[0] ?? { rows: [], areas: [] };

  // Live seat counts per category and total
  const stats = React.useMemo(() => {
    let totalSeats = 0;
    const catCounts = {};
    (plan?.categories ?? []).forEach((c) => {
      catCounts[c.name] = 0;
    });
    (plan?.zones ?? []).forEach((zone) => {
      (zone.rows ?? []).forEach((row) => {
        (row.seats ?? []).forEach((seat) => {
          totalSeats++;
          if (seat.category) {
            catCounts[seat.category] = (catCounts[seat.category] ?? 0) + 1;
          }
        });
      });
    });

    return { totalSeats, catCounts };
  }, [plan?.zones, plan?.categories]);

  // Stepper helper
  const renderStepper = (
    value,
    onChange,
    step = 10,
    min = -5000,
    max = 5000,
  ) => (
    <HStack spacing={1} maxW="150px">
      <Button
        size="xs"
        variant="outline"
        onClick={() => onChange(Math.max(min, Number(value) - step))}
      >
        -
      </Button>
      <Input
        size="xs"
        textAlign="center"
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => {
          if (e.target.value === '') {
            onChange('');
            return;
          }
          if (e.target.value === '-') {
            onChange('-');
            return;
          }
          const v = Number(e.target.value);
          if (!isNaN(v)) onChange(v);
        }}
        bg={inputBg}
      />
      <Button
        size="xs"
        variant="outline"
        onClick={() => onChange(Math.min(max, Number(value) + step))}
      >
        +
      </Button>
    </HStack>
  );

  const handleAddZoneSubmit = () => {
    if (!newZoneName.trim()) return;
    onAddZone(newZoneName.trim());
    setNewZoneName('');
    onCloseAddZone();
  };

  const handleAddCategorySubmit = () => {
    if (!newCatName.trim()) return;
    onAddCategory({ 
      uuid: Math.random().toString(36).substring(2, 9),
      name: newCatName.trim(), 
      color: newCatColor 
    });
    setNewCatName('');
    onCloseCatModal();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onUpdateBackground({
        background_image_url: reader.result,
        background_settings: {
          opacity: 0.8,
          x: 0,
          y: 0,
          width: plan?.size?.width ?? 900,
          height: plan?.size?.height ?? 900,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  // Selected row object if a row is explicitly selected
  const selectedRow =
    selectedRowIndex !== null && currentZone?.rows?.[selectedRowIndex]
      ? currentZone.rows[selectedRowIndex]
      : null;

  // Selected single seat object if a single seat is selected
  const selectedSingleSeat =
    selectedSeatGuids.length === 1 && currentZone?.rows
      ? currentZone.rows
        .flatMap((r) => r.seats ?? [])
        .find((s) => s.seat_guid === selectedSeatGuids[0])
      : null;

  // Find parent row if all selected seats belong to a single row
  const singleRowOfSelectedSeats = React.useMemo(() => {
    if (!selectedSeatGuids.length ?? !currentZone?.rows) return null;
    for (let i = 0; i < currentZone.rows.length; i++) {
      const row = currentZone.rows[i];
      const rowGuidSet = new Set((row.seats ?? []).map((s) => s.seat_guid));
      if (selectedSeatGuids.every((g) => rowGuidSet.has(g))) {
        return { row, rowIndex: i };
      }
    }
    return null;
  }, [selectedSeatGuids, currentZone?.rows]);

  // Find parent row of selected single seat
  const selectedSeatParentRowInfo = React.useMemo(() => {
    if (!selectedSingleSeat ?? !currentZone?.rows) return null;
    return singleRowOfSelectedSeats;
  }, [selectedSingleSeat, singleRowOfSelectedSeats, currentZone?.rows]);

  // Effective row: either explicitly selected or all selected seats belong to this row
  const effectiveRow =
    selectedRow ??
    (selectedSeatGuids.length > 1 && singleRowOfSelectedSeats
      ? singleRowOfSelectedSeats.row
      : null);
  const effectiveRowIndex =
    selectedRowIndex !== null
      ? selectedRowIndex
      : selectedSeatGuids.length > 1 && singleRowOfSelectedSeats
        ? singleRowOfSelectedSeats.rowIndex
        : null;

  // Selected area object if an area is selected (use first index when multiple selected)
  const selectedArea =
    Array.isArray(selectedAreaIndices) &&
      selectedAreaIndices.length > 0 &&
      currentZone?.areas?.[selectedAreaIndices[0]]
      ? currentZone.areas[selectedAreaIndices[0]]
      : null;

  // Use first selected index for editing when multiple are selected
  const editingAreaIndex =
    Array.isArray(selectedAreaIndices) && selectedAreaIndices.length > 0
      ? selectedAreaIndices[0]
      : null;

  return (
    <Box
      w="330px"
      h="100%"
      bg={bg}
      borderLeft="1px solid"
      borderColor={borderColor}
      display="flex"
      flexDirection="column"
      overflowY="auto"
      fontSize="sm"
      p={3}
      zIndex={10}
    >
      <VStack spacing={4} align="stretch">
        {/* =========================================
            SECTION 1: ZONES
           ========================================= */}
        <Box>
          <Flex justify="space-between" align="center" mb={2}>
            <HStack spacing={1}>
              <Text fontWeight="700" color={sectionHeaderColor}>
                Zones
              </Text>
              <Tooltip
                label="Organize venue by floors/balconies"
                placement="top"
              >
                <Badge fontSize="10px" borderRadius="full">
                  {zones.length}
                </Badge>
              </Tooltip>
            </HStack>
            <IconButton
              icon={<MdAdd />}
              size="xs"
              variant="ghost"
              aria-label="Add Zone"
              onClick={onOpenAddZone}
            />
          </Flex>

          <VStack spacing={1} align="stretch">
            {zones.map((zone, zIdx) => {
              const isActive = zIdx === activeZoneIndex;
              const isLocked = Boolean(zone.is_locked);

              return (
                <Flex
                  key={zIdx}
                  align="center"
                  justify="space-between"
                  px={2}
                  py={1.5}
                  borderRadius="6px"
                  bg={isActive ? activeZoneBg : 'transparent'}
                  _hover={{ bg: isActive ? activeZoneBg : itemHoverBg }}
                  cursor="pointer"
                  onClick={() => setActiveZoneIndex(zIdx)}
                >
                  <HStack spacing={1.5} overflow="hidden">
                    <Text color={isActive ? 'brand.600' : textColorSecondary}>
                      ❖
                    </Text>
                    <Text
                      fontWeight={isActive ? '700' : '500'}
                      color={isActive ? 'brand.600' : sectionHeaderColor}
                      noOfLines={1}
                      maxW="110px"
                    >
                      {zone.name}
                    </Text>
                  </HStack>

                  {/* Zone Actions matching prompt: Up, Down, Edit, Lock, Delete */}
                  <HStack spacing={0.5} onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      icon={<MdArrowUpward />}
                      size="xs"
                      variant="ghost"
                      aria-label="Move Up"
                      isDisabled={
                        zIdx === 0 ??
                        isLocked ??
                        Boolean(zones[zIdx - 1]?.is_locked)
                      }
                      onClick={() => onMoveZoneUp(zIdx)}
                    />
                    <IconButton
                      icon={<MdArrowDownward />}
                      size="xs"
                      variant="ghost"
                      aria-label="Move Down"
                      isDisabled={
                        zIdx === zones.length - 1 ??
                        isLocked ??
                        Boolean(zones[zIdx + 1]?.is_locked)
                      }
                      onClick={() => onMoveZoneDown(zIdx)}
                    />
                    <IconButton
                      icon={<MdEdit />}
                      size="xs"
                      variant="ghost"
                      aria-label="Edit Zone"
                      onClick={onOpenEditZone}
                    />
                    <IconButton
                      icon={isLocked ? <MdLock /> : <MdLockOpen />}
                      size="xs"
                      variant="ghost"
                      color={isLocked ? 'red.500' : 'gray.400'}
                      aria-label="Lock Zone"
                      onClick={() => onToggleLockZone(zIdx)}
                    />
                    {zones.length > 1 && (
                      <IconButton
                        icon={<MdDelete />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete Zone"
                        onClick={() => onDeleteZone(zIdx)}
                      />
                    )}
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* =========================================
            DYNAMIC CONTEXTUAL INSPECTOR
           ========================================= */}

        {/* CASE A: AN ENTIRE ROW IS SELECTED */}
        {effectiveRow ? (
          <Box
            bg={inputBg}
            p={3}
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
          >
            {/* Row Header with Title, Seat Count & Add/Remove Seat Actions */}
            <Flex justify="space-between" align="center" mb={3}>
              <Box>
                <Text fontWeight="700" color="brand.500" lineHeight="1.2">
                  Row
                </Text>
                <Text fontSize="10px" color={textColorSecondary}>
                  {effectiveRow.seats?.length ?? 0} seats
                </Text>
              </Box>
              <HStack spacing={1}>
                <Button
                  size="xs"
                  colorScheme="brand"
                  leftIcon={<MdAdd />}
                  h="24px"
                  fontSize="11px"
                  onClick={() =>
                    onAddSeatToRow && onAddSeatToRow(effectiveRowIndex)
                  }
                  title="Add a new seat at the end of this row"
                >
                  Add Seat
                </Button>
                {effectiveRow.seats && effectiveRow.seats.length > 1 && (
                  <IconButton
                    size="xs"
                    variant="outline"
                    colorScheme="red"
                    h="24px"
                    w="24px"
                    icon={<MdDelete />}
                    aria-label="Remove Last Seat"
                    title="Remove last seat from this row"
                    onClick={() =>
                      onRemoveSeatFromRow &&
                      onRemoveSeatFromRow(effectiveRowIndex)
                    }
                  />
                )}
              </HStack>
            </Flex>

            <VStack spacing={2.5} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Category
                </FormLabel>
                <Select
                  size="xs"
                  value={
                    effectiveRow.category ??
                    effectiveRow.seats?.[0]?.category ??
                    ''
                  }
                  onChange={(e) =>
                    onUpdateRowProperty(
                      effectiveRowIndex,
                      'category',
                      e.target.value,
                    )
                  }
                >
                  <option value="">(None - Unassigned)</option>
                  {categories.map((c, i) => (
                    <option key={i} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (x)
                </Text>
                {renderStepper(
                  effectiveRow.position?.x ?? 0,
                  (x) =>
                    onUpdateRowProperty(effectiveRowIndex, 'position', {
                      ...(effectiveRow.position ?? {}),
                      x,
                    }),
                  10,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (y)
                </Text>
                {renderStepper(
                  effectiveRow.position?.y ?? 0,
                  (y) =>
                    onUpdateRowProperty(effectiveRowIndex, 'position', {
                      ...(effectiveRow.position ?? {}),
                      y,
                    }),
                  10,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Rotation
                </Text>
                {renderStepper(
                  effectiveRow.rotation ?? 0,
                  (rot) =>
                    onUpdateRowProperty(effectiveRowIndex, 'rotation', rot),
                  15,
                  -360,
                  360,
                )}
              </Flex>

              {/* Seating Spacing */}
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Seat spacing
                </Text>
                {renderStepper(
                  effectiveRow.seat_spacing ?? 25,
                  (s) =>
                    onUpdateRowProperty(effectiveRowIndex, 'seat_spacing', s),
                  2,
                  10,
                  150,
                )}
              </Flex>

              {/* Curve Row Control */}
              <Box
                bg={curveBoxBg}
                p={2.5}
                borderRadius="6px"
                border="1px solid"
                borderColor={borderColor}
              >
                <Flex justify="space-between" align="center" mb={1}>
                  <HStack spacing={1}>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color={sectionHeaderColor}
                    >
                      Curve row
                    </Text>
                    {Boolean(effectiveRow.curve_angle) && (
                      <Badge fontSize="10px" colorScheme="purple">
                        {effectiveRow.curve_angle}°
                      </Badge>
                    )}
                  </HStack>
                  {Boolean(effectiveRow.curve_angle) && (
                    <Button
                      size="xs"
                      h="18px"
                      fontSize="9px"
                      variant="ghost"
                      onClick={() =>
                        onUpdateRowProperty(effectiveRowIndex, 'curve_angle', 0)
                      }
                    >
                      Straight (0°)
                    </Button>
                  )}
                </Flex>

                <Slider
                  min={-360}
                  max={360}
                  step={5}
                  value={effectiveRow.curve_angle ?? 0}
                  onChange={(val) =>
                    onUpdateRowProperty(effectiveRowIndex, 'curve_angle', val)
                  }
                >
                  <SliderTrack>
                    <SliderFilledTrack bg="brand.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={3.5} />
                </Slider>

                <Flex
                  justify="space-between"
                  align="center"
                  mt={1}
                  gap={1}
                  wrap="wrap"
                >
                  <Button
                    size="xs"
                    h="18px"
                    px={1}
                    fontSize="9px"
                    variant="outline"
                    onClick={() =>
                      onUpdateRowProperty(
                        effectiveRowIndex,
                        'curve_angle',
                        -360,
                      )
                    }
                    title="Full circle upward (-360°)"
                  >
                    -360°
                  </Button>
                  <Button
                    size="xs"
                    h="18px"
                    px={1}
                    fontSize="9px"
                    variant="outline"
                    onClick={() =>
                      onUpdateRowProperty(
                        effectiveRowIndex,
                        'curve_angle',
                        -180,
                      )
                    }
                    title="Half circle upward (-180°)"
                  >
                    -180°
                  </Button>
                  <Button
                    size="xs"
                    h="18px"
                    px={1}
                    fontSize="9px"
                    variant={!effectiveRow.curve_angle ? 'solid' : 'outline'}
                    colorScheme={!effectiveRow.curve_angle ? 'brand' : 'gray'}
                    onClick={() =>
                      onUpdateRowProperty(effectiveRowIndex, 'curve_angle', 0)
                    }
                    title="Straight (0°)"
                  >
                    0°
                  </Button>
                  <Button
                    size="xs"
                    h="18px"
                    px={1}
                    fontSize="9px"
                    variant="outline"
                    onClick={() =>
                      onUpdateRowProperty(effectiveRowIndex, 'curve_angle', 180)
                    }
                    title="Half circle downward (+180°)"
                  >
                    +180°
                  </Button>
                  <Button
                    size="xs"
                    h="18px"
                    px={1}
                    fontSize="9px"
                    variant="outline"
                    onClick={() =>
                      onUpdateRowProperty(effectiveRowIndex, 'curve_angle', 360)
                    }
                    title="Full circle downward (+360°)"
                  >
                    +360°
                  </Button>
                </Flex>
              </Box>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Show row label
                </Text>
                <Switch
                  size="sm"
                  isChecked={Boolean(effectiveRow.show_row_numbers)}
                  onChange={(e) =>
                    onUpdateRowProperty(
                      effectiveRowIndex,
                      'show_row_numbers',
                      e.target.checked,
                    )
                  }
                />
              </Flex>

              {effectiveRow.show_row_numbers && (
                <div>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Row label
                    </Text>
                    <Input
                      size="xs"
                      maxW="130px"
                      value={
                        effectiveRow.row_label !== undefined
                          ? effectiveRow.row_label
                          : String(effectiveRow.row_number)
                      }
                      onChange={(e) =>
                        onUpdateRowProperty(
                          effectiveRowIndex,
                          'row_label',
                          e.target.value,
                        )
                      }
                    />
                  </Flex>

                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Label position
                    </Text>
                    <HStack spacing={1}>
                      {(() => {
                        const isRowCircular =
                          Math.abs(Number(effectiveRow.curve_angle || 0)) >= 359.5 ||
                          Boolean(effectiveRow.is_circular);
                        const currentPos = effectiveRow.row_label_position ?? 'both';

                        return (
                          <>
                            <Button
                              size="xs"
                              isDisabled={isRowCircular}
                              title={isRowCircular ? 'Circular rows only show a single label' : 'Show label on both sides'}
                              variant={
                                (!isRowCircular && currentPos === 'both')
                                  ? 'solid'
                                  : 'ghost'
                              }
                              colorScheme={
                                (!isRowCircular && currentPos === 'both')
                                  ? 'brand'
                                  : 'gray'
                              }
                              onClick={() =>
                                onUpdateRowProperty(
                                  effectiveRowIndex,
                                  'row_label_position',
                                  'both',
                                )
                              }
                            >
                              Both
                            </Button>
                            <Button
                              size="xs"
                              variant={
                                currentPos === 'left' || (isRowCircular && currentPos === 'both')
                                  ? 'solid'
                                  : 'ghost'
                              }
                              colorScheme={
                                currentPos === 'left' || (isRowCircular && currentPos === 'both')
                                  ? 'brand'
                                  : 'gray'
                              }
                              onClick={() =>
                                onUpdateRowProperty(
                                  effectiveRowIndex,
                                  'row_label_position',
                                  'left',
                                )
                              }
                            >
                              Left
                            </Button>
                            <Button
                              size="xs"
                              variant={
                                currentPos === 'right'
                                  ? 'solid'
                                  : 'ghost'
                              }
                              colorScheme={
                                currentPos === 'right'
                                  ? 'brand'
                                  : 'gray'
                              }
                              onClick={() =>
                                onUpdateRowProperty(
                                  effectiveRowIndex,
                                  'row_label_position',
                                  'right',
                                )
                              }
                            >
                              Right
                            </Button>
                          </>
                        );
                      })()}
                    </HStack>
                  </Flex>
                </div>
              )}

              <Divider borderColor={borderColor} />

              <Flex justify="space-between" align="center" mb={1}>
                <Text fontWeight="600" fontSize="xs" color={sectionHeaderColor}>
                  Seat numbers
                </Text>
                {onOpenAutoLabel && (
                  <Button
                    size="xs"
                    h="22px"
                    fontSize="11px"
                    variant="ghost"
                    colorScheme="brand"
                    leftIcon={<MdFormatListNumbered size={13} />}
                    onClick={onOpenAutoLabel}
                  >
                    Auto Label Tool
                  </Button>
                )}
              </Flex>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Numbering
                </FormLabel>
                <Select
                  size="xs"
                  value={effectiveRow.numbering_pattern ?? '1, 2, 3, …'}
                  onChange={(e) =>
                    onUpdateRowProperty(
                      effectiveRowIndex,
                      'numbering_pattern',
                      e.target.value,
                    )
                  }
                >
                  <option value="1, 2, 3, …">1, 2, 3, …</option>
                  <option value="A, B, C, …">A, B, C, …</option>
                </Select>
              </FormControl>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Starting at
                </Text>
                {(() => {
                  const firstSeatNumStr = effectiveRow.seats?.[0]?.seat_number;
                  const isLetters = effectiveRow.numbering_pattern === 'A, B, C, …';
                  let defaultVal;
                  if (isLetters) {
                    defaultVal = (firstSeatNumStr && isNaN(Number(firstSeatNumStr))) ? firstSeatNumStr : 'A';
                  } else {
                    defaultVal = (firstSeatNumStr && !isNaN(Number(firstSeatNumStr))) ? Number(firstSeatNumStr) : 1;
                  }

                  const startVal = effectiveRow.start_number ?? defaultVal;

                  return isLetters ? (
                    <HStack spacing={1}>
                      <IconButton
                        size="xs"
                        h="22px"
                        w="22px"
                        icon={<Text fontSize="xs" fontWeight="bold">-</Text>}
                        aria-label="Previous Letter"
                        onClick={() => {
                          const curCode = typeof startVal === 'string' && isNaN(Number(startVal)) ? startVal.charCodeAt(0) : 65;
                          const prevCode = Math.max(65, curCode - 1);
                          onUpdateRowProperty(effectiveRowIndex, 'start_number', String.fromCharCode(prevCode));
                        }}
                      />
                      <Input
                        size="xs"
                        maxW="36px"
                        h="22px"
                        maxLength={1}
                        value={startVal}
                        textAlign="center"
                        fontWeight="700"
                        textTransform="uppercase"
                        onChange={(e) => {
                          const ch = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
                          if (ch) onUpdateRowProperty(effectiveRowIndex, 'start_number', ch);
                        }}
                      />
                      <IconButton
                        size="xs"
                        h="22px"
                        w="22px"
                        icon={<Text fontSize="xs" fontWeight="bold">+</Text>}
                        aria-label="Next Letter"
                        onClick={() => {
                          const curCode = typeof startVal === 'string' && isNaN(Number(startVal)) ? startVal.charCodeAt(0) : 64;
                          const nextCode = Math.min(90, curCode + 1);
                          onUpdateRowProperty(effectiveRowIndex, 'start_number', String.fromCharCode(nextCode));
                        }}
                      />
                    </HStack>
                  ) : (
                    renderStepper(
                      Number(startVal) || 1,
                      (num) => onUpdateRowProperty(effectiveRowIndex, 'start_number', num),
                      1,
                      1,
                      1000,
                    )
                  );
                })()}
              </Flex>

              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="xs" color={textColorSecondary}>
                    Reversed
                  </Text>
                  <Text fontSize="9px" color="gray.400">
                    Right to left numbering
                  </Text>
                </Box>
                <Switch
                  size="sm"
                  isChecked={Boolean(effectiveRow.reversed)}
                  onChange={(e) =>
                    onUpdateRowProperty(
                      effectiveRowIndex,
                      'reversed',
                      e.target.checked,
                    )
                  }
                />
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Seat label
                </Text>
                <Input
                  size="xs"
                  maxW="130px"
                  placeholder="e.g. VIP"
                  value={effectiveRow.seat_label ?? ''}
                  onChange={(e) =>
                    onUpdateRowProperty(
                      effectiveRowIndex,
                      'seat_label',
                      e.target.value,
                    )
                  }
                />
              </Flex>

              <Divider borderColor={borderColor} />

              <Text fontWeight="600" fontSize="xs" color={sectionHeaderColor}>
                Seat
              </Text>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Radius
                </Text>
                {renderStepper(
                  effectiveRow.seat_radius ??
                  effectiveRow.seats?.[0]?.radius ??
                  10,
                  (r) =>
                    onUpdateRowProperty(effectiveRowIndex, 'seat_radius', r),
                  1,
                  4,
                  50,
                )}
              </Flex>


            </VStack>
          </Box>
        ) : selectedSingleSeat ? (
          /* CASE B: A SINGLE SEAT IS SELECTED */
          <Box
            bg={inputBg}
            p={3}
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="700" color="brand.500">
                Seat
              </Text>
              {selectedSeatParentRowInfo && onSelectRow && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="brand"
                  fontSize="11px"
                  h="22px"
                  onClick={() =>
                    onSelectRow(selectedSeatParentRowInfo.rowIndex)
                  }
                  title="Click to view and edit this Row's settings (Seat spacing, Rotation, Numbering)"
                >
                  Row{' '}
                  {selectedSeatParentRowInfo.row.row_number ??
                    selectedSeatParentRowInfo.rowIndex + 1}{' '}
                  Settings
                </Button>
              )}
            </Flex>

            <VStack spacing={2.5} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (x)
                </Text>
                {renderStepper(
                  selectedSingleSeat.position?.x ?? 0,
                  (x) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'position',
                      {
                        ...(selectedSingleSeat.position ?? {}),
                        x,
                      },
                    ),
                  5,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (y)
                </Text>
                {renderStepper(
                  selectedSingleSeat.position?.y ?? 0,
                  (y) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'position',
                      {
                        ...(selectedSingleSeat.position ?? {}),
                        y,
                      },
                    ),
                  5,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Radius
                </Text>
                {renderStepper(
                  selectedSingleSeat.radius ?? 10,
                  (r) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'radius',
                      r,
                    ),
                  1,
                  4,
                  50,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Seat number
                </Text>
                <Input
                  size="xs"
                  maxW="130px"
                  value={selectedSingleSeat.seat_number ?? ''}
                  onChange={(e) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'seat_number',
                      e.target.value,
                    )
                  }
                />
              </Flex>

              {/* Seat ID: System-managed unique identifier */}
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="xs" color={textColorSecondary}>
                    Seat ID
                  </Text>
                  <Text fontSize="9px" color="gray.400">
                    Auto (Zone-Row-Seat)
                  </Text>
                </Box>
                <Badge
                  fontSize="10px"
                  px={2}
                  py={1}
                  borderRadius="4px"
                  colorScheme="purple"
                  maxW="140px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  title={selectedSingleSeat.seat_guid ?? ''}
                >
                  {selectedSingleSeat.seat_guid ?? 'N/A'}
                </Badge>
              </Flex>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Category
                </FormLabel>
                <Select
                  size="xs"
                  value={selectedSingleSeat.category ?? ''}
                  onChange={(e) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'category',
                      e.target.value,
                    )
                  }
                >
                  <option value="">(None - Unassigned)</option>
                  {categories.map((c, i) => (
                    <option key={i} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Optimizer hint dropdown matching prompt: < , <> , > */}
              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Optimizer hint
                </FormLabel>
                <Select
                  size="xs"
                  value={selectedSingleSeat.optimizer_hint ?? ''}
                  onChange={(e) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'optimizer_hint',
                      e.target.value,
                    )
                  }
                >
                  <option value="">(None)</option>
                  <option value="<">&lt;</option>
                  <option value="<>">&lt;&gt;</option>
                  <option value=">">&gt;</option>
                </Select>
              </FormControl>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Rotation
                </Text>
                {renderStepper(
                  selectedSingleSeat.rotation ?? 0,
                  (rot) =>
                    onUpdateSingleSeatProperty(
                      selectedSingleSeat.seat_guid,
                      'rotation',
                      rot,
                    ),
                  15,
                  -360,
                  360,
                )}
              </Flex>
            </VStack>
          </Box>
        ) : selectedSeatGuids.length > 1 ? (
          /* CASE C: MULTIPLE SEATS ARE SELECTED */
          <Box
            bg={inputBg}
            p={3}
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="700" color="brand.500">
                Multiple Seats
              </Text>
              <Badge
                colorScheme="blue"
                fontSize="11px"
                px={2}
                py={0.5}
                borderRadius="4px"
              >
                {selectedSeatGuids.length} selected
              </Badge>
            </Flex>

            <VStack spacing={2.5} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Category
                </FormLabel>
                <Select
                  size="xs"
                  placeholder="Set category for all..."
                  onChange={(e) => {
                    if (onBatchUpdateSeats) {
                      onBatchUpdateSeats(selectedSeatGuids, {
                        category: e.target.value,
                      });
                    }
                  }}
                >
                  <option value="">(None - Unassigned)</option>
                  {categories.map((c, i) => (
                    <option key={i} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Optimizer hint
                </FormLabel>
                <Select
                  size="xs"
                  onChange={(e) => {
                    if (onBatchUpdateSeats) {
                      onBatchUpdateSeats(selectedSeatGuids, {
                        optimizer_hint: e.target.value,
                      });
                    }
                  }}
                >
                  <option value="">(None)</option>
                  <option value="<">&lt; (Fill from left)</option>
                  <option value="<>">&lt;&gt; (Fill from center)</option>
                  <option value=">">&gt; (Fill from right)</option>
                </Select>
              </FormControl>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Radius
                </Text>
                {renderStepper(
                  10,
                  (r) => {
                    if (onBatchUpdateSeats) {
                      onBatchUpdateSeats(selectedSeatGuids, { radius: r });
                    }
                  },
                  1,
                  4,
                  50,
                )}
              </Flex>

              <Divider borderColor={borderColor} my={2} />

              {onOpenAutoLabel && (
                <Box mb={2}>
                  <Button
                    size="xs"
                    colorScheme="brand"
                    variant="solid"
                    w="100%"
                    leftIcon={<MdFormatListNumbered size={14} />}
                    onClick={onOpenAutoLabel}
                  >
                    Auto Label Rows & Number Seats
                  </Button>
                  <Text fontSize="10px" color="gray.400" mt={1} textAlign="center">
                    Label rows and number seats in visual top-to-bottom order
                  </Text>
                </Box>
              )}

              <Box>
                <Text fontWeight="600" fontSize="xs" color={sectionHeaderColor} mb={1}>
                  Transform Parent Rows
                </Text>
                <Text fontSize="10px" color="gray.400" mb={3} lineHeight="1.2">
                  Rotate or move all rows that contain the selected seats.
                </Text>

                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" color={textColorSecondary}>
                    Rotate (abs. degrees)
                  </Text>
                  <Input
                    size="xs"
                    type="number"
                    maxW="80px"
                    placeholder="e.g. 45"
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        const parentRowIndices = [...new Set(
                          (currentZone?.rows ?? []).map((r, rIdx) =>
                            r.seats?.some(s => selectedSeatGuids.includes(s.seat_guid)) ? rIdx : -1
                          ).filter(idx => idx !== -1)
                        )];
                        parentRowIndices.forEach((rIdx) => {
                          onUpdateRowProperty(rIdx, 'rotation', val);
                        });
                      }
                    }}
                  />
                </Flex>

                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" color={textColorSecondary}>
                    Shift X (delta)
                  </Text>
                  <Input
                    size="xs"
                    type="number"
                    maxW="80px"
                    placeholder="+/- X"
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        const parentRowIndices = [...new Set(
                          (currentZone?.rows ?? []).map((r, rIdx) =>
                            r.seats?.some(s => selectedSeatGuids.includes(s.seat_guid)) ? rIdx : -1
                          ).filter(idx => idx !== -1)
                        )];
                        parentRowIndices.forEach((rIdx) => {
                          const r = currentZone.rows[rIdx];
                          onUpdateRowProperty(rIdx, 'position', { ...(r.position ?? { x: 0, y: 0 }), x: (r.position?.x ?? 0) + val });
                        });
                        e.target.value = ''; // Reset after applying delta
                      }
                    }}
                  />
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text fontSize="xs" color={textColorSecondary}>
                    Shift Y (delta)
                  </Text>
                  <Input
                    size="xs"
                    type="number"
                    maxW="80px"
                    placeholder="+/- Y"
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        const parentRowIndices = [...new Set(
                          (currentZone?.rows ?? []).map((r, rIdx) =>
                            r.seats?.some(s => selectedSeatGuids.includes(s.seat_guid)) ? rIdx : -1
                          ).filter(idx => idx !== -1)
                        )];
                        parentRowIndices.forEach((rIdx) => {
                          const r = currentZone.rows[rIdx];
                          onUpdateRowProperty(rIdx, 'position', { ...(r.position ?? { x: 0, y: 0 }), y: (r.position?.y ?? 0) + val });
                        });
                        e.target.value = ''; // Reset after applying delta
                      }
                    }}
                  />
                </Flex>
              </Box>

              <Divider borderColor={borderColor} my={1} />

              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                leftIcon={<MdDelete />}
                onClick={onDeleteSelectedSeats}
              >
                Delete {selectedSeatGuids.length} Seats
              </Button>
            </VStack>
          </Box>
        ) : selectedArea ? (
          /* CASE C: A SHAPE / AREA OR STANDALONE TEXT IS SELECTED */
          <Box
            bg={inputBg}
            p={3}
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontWeight="700" color="brand.500">
                {selectedArea.shape === 'text' ? 'Text' : 'Shape'}
              </Text>
              <Badge colorScheme="purple" fontSize="10px">
                {selectedArea.shape ?? 'rectangle'}
              </Badge>
            </Flex>

            <VStack spacing={2.5} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (x)
                </Text>
                {renderStepper(
                  selectedArea.position?.x ?? 0,
                  (x) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      position: { ...(selectedArea.position ?? {}), x },
                    }),
                  10,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Position (y)
                </Text>
                {renderStepper(
                  selectedArea.position?.y ?? 0,
                  (y) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      position: { ...(selectedArea.position ?? {}), y },
                    }),
                  10,
                  -5000,
                  5000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Rotation
                </Text>
                {renderStepper(
                  selectedArea.rotation ?? 0,
                  (rot) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      rotation: rot,
                    }),
                  15,
                  -360,
                  360,
                )}
              </Flex>

              {/* SCALE / SIZE CONTROLS FOR SHAPES */}
              {selectedArea.shape !== 'text' && (
                <>
                  <Divider borderColor={borderColor} />
                  <Text
                    fontWeight="600"
                    fontSize="xs"
                    color={sectionHeaderColor}
                  >
                    Dimensions &amp; Scale
                  </Text>

                  {/* Proportional Scale Controls */}
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Scale
                    </Text>
                    <HStack spacing={1}>
                      {[
                        { label: '-25%', factor: 0.75 },
                        { label: '-10%', factor: 0.9 },
                        { label: '+10%', factor: 1.1 },
                        { label: '+25%', factor: 1.25 },
                      ].map(({ label, factor }) => (
                        <Button
                          key={label}
                          size="xs"
                          h="22px"
                          px={1.5}
                          fontSize="10px"
                          fontWeight="bold"
                          variant="outline"
                          onClick={() => {
                            const updated = { ...selectedArea };
                            if (
                              updated.shape === 'rectangle' ??
                              !updated.shape
                            ) {
                              updated.rectangle = {
                                width: Math.max(
                                  10,
                                  Math.round(
                                    (updated.rectangle?.width ?? 120) * factor,
                                  ),
                                ),
                                height: Math.max(
                                  10,
                                  Math.round(
                                    (updated.rectangle?.height ?? 60) * factor,
                                  ),
                                ),
                              };
                            } else if (updated.shape === 'circle') {
                              updated.circle = {
                                radius: Math.max(
                                  5,
                                  Math.round(
                                    (updated.circle?.radius ?? 50) * factor,
                                  ),
                                ),
                              };
                            } else if (updated.shape === 'ellipse') {
                              updated.ellipse = {
                                radius: {
                                  x: Math.max(
                                    5,
                                    Math.round(
                                      (updated.ellipse?.radius?.x ?? 50) *
                                      factor,
                                    ),
                                  ),
                                  y: Math.max(
                                    5,
                                    Math.round(
                                      (updated.ellipse?.radius?.y ?? 80) *
                                      factor,
                                    ),
                                  ),
                                },
                              };
                            } else if (updated.shape === 'polygon') {
                              updated.polygon = {
                                points: (updated.polygon?.points ?? []).map(
                                  (p) => ({
                                    x: Math.round(p.x * factor),
                                    y: Math.round(p.y * factor),
                                  }),
                                ),
                              };
                            }
                            onUpdateArea(editingAreaIndex, updated);
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </HStack>
                  </Flex>

                  {/* Type-specific dimension steppers */}
                  {(selectedArea.shape === 'rectangle' ??
                    !selectedArea.shape) && (
                      <>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="xs" color={textColorSecondary}>
                            Width
                          </Text>
                          {renderStepper(
                            selectedArea.rectangle?.width ?? 120,
                            (w) =>
                              onUpdateArea(editingAreaIndex, {
                                ...selectedArea,
                                rectangle: {
                                  ...(selectedArea.rectangle ?? {}),
                                  width: Math.max(10, w),
                                },
                              }),
                            10,
                            10,
                            5000,
                          )}
                        </Flex>

                        <Flex justify="space-between" align="center">
                          <Text fontSize="xs" color={textColorSecondary}>
                            Height
                          </Text>
                          {renderStepper(
                            selectedArea.rectangle?.height ?? 60,
                            (h) =>
                              onUpdateArea(editingAreaIndex, {
                                ...selectedArea,
                                rectangle: {
                                  ...(selectedArea.rectangle ?? {}),
                                  height: Math.max(10, h),
                                },
                              }),
                            10,
                            10,
                            5000,
                          )}
                        </Flex>
                      </>
                    )}

                  {selectedArea.shape === 'circle' && (
                    <Flex justify="space-between" align="center">
                      <Text fontSize="xs" color={textColorSecondary}>
                        Radius
                      </Text>
                      {renderStepper(
                        selectedArea.circle?.radius ?? 50,
                        (r) =>
                          onUpdateArea(editingAreaIndex, {
                            ...selectedArea,
                            circle: {
                              ...(selectedArea.circle ?? {}),
                              radius: Math.max(5, r),
                            },
                          }),
                        5,
                        5,
                        2000,
                      )}
                    </Flex>
                  )}

                  {selectedArea.shape === 'ellipse' && (
                    <>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" color={textColorSecondary}>
                          Radius (x)
                        </Text>
                        {renderStepper(
                          selectedArea.ellipse?.radius?.x ?? 50,
                          (rx) =>
                            onUpdateArea(editingAreaIndex, {
                              ...selectedArea,
                              ellipse: {
                                ...(selectedArea.ellipse ?? {}),
                                radius: {
                                  ...(selectedArea.ellipse?.radius ?? {}),
                                  x: Math.max(5, rx),
                                },
                              },
                            }),
                          5,
                          5,
                          2000,
                        )}
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" color={textColorSecondary}>
                          Radius (y)
                        </Text>
                        {renderStepper(
                          selectedArea.ellipse?.radius?.y ?? 80,
                          (ry) =>
                            onUpdateArea(editingAreaIndex, {
                              ...selectedArea,
                              ellipse: {
                                ...(selectedArea.ellipse ?? {}),
                                radius: {
                                  ...(selectedArea.ellipse?.radius ?? {}),
                                  y: Math.max(5, ry),
                                },
                              },
                            }),
                          5,
                          5,
                          2000,
                        )}
                      </Flex>
                    </>
                  )}

                  <Divider borderColor={borderColor} />
                </>
              )}

              {selectedArea.shape !== 'text' && (
                <>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Color
                    </Text>
                    <ColorPickerPopover
                      value={selectedArea.color ?? '#CCCCCC'}
                      onChange={(c) =>
                        onUpdateArea(editingAreaIndex, {
                          ...selectedArea,
                          color: c,
                        })
                      }
                    />
                  </Flex>

                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Border
                    </Text>
                    <ColorPickerPopover
                      value={selectedArea.border_color ?? '#000001'}
                      onChange={(c) =>
                        onUpdateArea(editingAreaIndex, {
                          ...selectedArea,
                          border_color: c,
                        })
                      }
                    />
                  </Flex>

                  {/* Opacity slider */}
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={textColorSecondary}>
                      Opacity
                    </Text>
                    <HStack spacing={2}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={Math.round((selectedArea.opacity ?? 0.9) * 100)}
                        onChange={(e) =>
                          onUpdateArea(editingAreaIndex, {
                            ...selectedArea,
                            opacity: Number(e.target.value) / 100,
                          })
                        }
                        style={{ width: '80px' }}
                      />
                      <Text
                        fontSize="xs"
                        w="32px"
                        textAlign="right"
                        color={textColorSecondary}
                      >
                        {Math.round((selectedArea.opacity ?? 0.9) * 100)}%
                      </Text>
                    </HStack>
                  </Flex>
                </>
              )}

              {/* Text settings for shape or standalone text */}
              <Divider borderColor={borderColor} />
              <Text fontWeight="600" fontSize="xs" color={sectionHeaderColor}>
                Text
              </Text>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} color={textColorSecondary}>
                  Content
                </FormLabel>
                <Input
                  size="xs"
                  value={selectedArea.text?.text ?? ''}
                  placeholder="Text label..."
                  onChange={(e) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      text: {
                        ...(selectedArea.text ?? {}),
                        text: e.target.value,
                      },
                    })
                  }
                />
              </FormControl>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Text size
                </Text>
                {renderStepper(
                  selectedArea.text?.fontSize ?? 16,
                  (size) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      text: { ...(selectedArea.text ?? {}), fontSize: size },
                    }),
                  2,
                  8,
                  96,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Text position (x)
                </Text>
                {renderStepper(
                  selectedArea.text?.position?.x ?? 0,
                  (x) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      text: {
                        ...(selectedArea.text ?? {}),
                        position: { ...(selectedArea.text?.position ?? {}), x },
                      },
                    }),
                  10,
                  -1000,
                  1000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Text position (y)
                </Text>
                {renderStepper(
                  selectedArea.text?.position?.y ?? 0,
                  (y) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      text: {
                        ...(selectedArea.text ?? {}),
                        position: { ...(selectedArea.text?.position ?? {}), y },
                      },
                    }),
                  10,
                  -1000,
                  1000,
                )}
              </Flex>

              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={textColorSecondary}>
                  Text color
                </Text>
                <ColorPickerPopover
                  value={selectedArea.text?.color ?? '#333333'}
                  onChange={(c) =>
                    onUpdateArea(editingAreaIndex, {
                      ...selectedArea,
                      text: { ...(selectedArea.text ?? {}), color: c },
                    })
                  }
                />
              </Flex>

              {/* Stacking / Layer Order */}
              <Divider borderColor={borderColor} />
              <Text fontWeight="600" fontSize="xs" color={sectionHeaderColor}>
                Stacking order
              </Text>

              <HStack spacing={1}>
                <Button
                  size="xs"
                  variant="outline"
                  flex={1}
                  onClick={() => onReorderAreaLayer(editingAreaIndex, 'front')}
                >
                  To Front
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  flex={1}
                  onClick={() =>
                    onReorderAreaLayer(editingAreaIndex, 'forward')
                  }
                >
                  Forward
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  flex={1}
                  onClick={() =>
                    onReorderAreaLayer(editingAreaIndex, 'backward')
                  }
                >
                  Backward
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  flex={1}
                  onClick={() => onReorderAreaLayer(editingAreaIndex, 'back')}
                >
                  To Back
                </Button>
              </HStack>
            </VStack>
          </Box>
        ) : (
          /* CASE D: NOTHING SELECTED (DEFAULT PLAN SETTINGS) */
          <>
            <Box>
              <Text fontWeight="700" color={sectionHeaderColor} mb={2}>
                Plan
              </Text>
              <VStack spacing={2} align="stretch">
                <Flex justify="space-between" align="center">
                  <Text color={textColorSecondary}>Name</Text>
                  <Input
                    size="xs"
                    maxW="150px"
                    value={plan?.name ?? ''}
                    onChange={(e) => onUpdatePlanName(e.target.value)}
                    bg={inputBg}
                  />
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text color={textColorSecondary}>Width</Text>
                  {renderStepper(
                    plan?.size?.width ?? 900,
                    (w) => onUpdatePlanDimensions(w, plan?.size?.height ?? 900),
                    50,
                    200,
                    5000,
                  )}
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text color={textColorSecondary}>Height</Text>
                  {renderStepper(
                    plan?.size?.height ?? 900,
                    (h) => onUpdatePlanDimensions(plan?.size?.width ?? 900, h),
                    50,
                    200,
                    5000,
                  )}
                </Flex>

                <Flex justify="space-between" align="center">
                  <Text color={textColorSecondary}>Number of seats</Text>
                  <Text fontWeight="700" color={sectionHeaderColor}>
                    {stats.totalSeats}
                  </Text>
                </Flex>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Categories list with counts */}
            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="700" color={sectionHeaderColor}>
                  Categories
                </Text>
                <IconButton
                  icon={<MdAdd />}
                  size="xs"
                  variant="ghost"
                  aria-label="Add Category"
                  onClick={onOpenCatModal}
                />
              </Flex>

              <VStack spacing={1.5} align="stretch">
                {categories.map((cat, cIdx) => (
                  <Flex
                    key={cIdx}
                    align="center"
                    justify="space-between"
                    px={2}
                    py={1}
                    borderRadius="6px"
                    _hover={{ bg: itemHoverBg }}
                    cursor="pointer"
                    onClick={() => {
                      setEditingCatIndex(cIdx);
                      setEditCatName(cat.name);
                      setEditCatColor(cat.color);
                    }}
                  >
                    <HStack spacing={2}>
                      <Box
                        w="12px"
                        h="12px"
                        borderRadius="full"
                        bg={cat.color}
                        flexShrink={0}
                      />
                      <Text
                        fontWeight="500"
                        color={sectionHeaderColor}
                        noOfLines={1}
                        maxW="150px"
                      >
                        {cat.name}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color={textColorSecondary}>
                      {stats.catCounts[cat.name] ?? 0} seats
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Background picture controls */}
            <Box>
              <Text fontWeight="700" color={sectionHeaderColor} mb={1}>
                Background picture
              </Text>
              <Text fontSize="xs" color={textColorSecondary} mb={2}>
                The background you upload here will not be saved as part of your
                plan, it is just useful for placing objects on the plan.
              </Text>

              <VStack spacing={2} align="stretch">
                <Button
                  size="xs"
                  leftIcon={<MdCloudUpload />}
                  variant="outline"
                  as="label"
                  cursor="pointer"
                  w="100%"
                >
                  Upload Blueprint
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </Button>

                {plan?.background_image_url && (
                  <>
                    <Flex justify="space-between" align="center">
                      <Text color={textColorSecondary}>Opacity (%)</Text>
                      {renderStepper(
                        Math.round(
                          (plan.background_settings?.opacity ?? 0.8) * 100,
                        ),
                        (op) =>
                          onUpdateBackground({
                            background_settings: {
                              ...plan.background_settings,
                              opacity: op / 100,
                            },
                          }),
                        10,
                        0,
                        100,
                      )}
                    </Flex>

                    <Flex justify="space-between" align="center">
                      <Text color={textColorSecondary}>Width</Text>
                      {renderStepper(
                        plan.background_settings?.width ?? 900,
                        (w) =>
                          onUpdateBackground({
                            background_settings: {
                              ...plan.background_settings,
                              width: w,
                            },
                          }),
                        50,
                        100,
                        5000,
                      )}
                    </Flex>

                    <Flex justify="space-between" align="center">
                      <Text color={textColorSecondary}>Height</Text>
                      {renderStepper(
                        plan.background_settings?.height ?? 900,
                        (h) =>
                          onUpdateBackground({
                            background_settings: {
                              ...plan.background_settings,
                              height: h,
                            },
                          }),
                        50,
                        100,
                        5000,
                      )}
                    </Flex>

                    <Flex justify="space-between" align="center">
                      <Text color={textColorSecondary}>Position (x)</Text>
                      {renderStepper(
                        plan.background_settings?.x ?? 0,
                        (x) =>
                          onUpdateBackground({
                            background_settings: {
                              ...plan.background_settings,
                              x,
                            },
                          }),
                        25,
                        -2000,
                        2000,
                      )}
                    </Flex>

                    <Flex justify="space-between" align="center">
                      <Text color={textColorSecondary}>Position (y)</Text>
                      {renderStepper(
                        plan.background_settings?.y ?? 0,
                        (y) =>
                          onUpdateBackground({
                            background_settings: {
                              ...plan.background_settings,
                              y,
                            },
                          }),
                        25,
                        -2000,
                        2000,
                      )}
                    </Flex>

                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() =>
                        onUpdateBackground({ background_image_url: '' })
                      }
                    >
                      Remove background
                    </Button>
                  </>
                )}
              </VStack>
            </Box>
          </>
        )}
      </VStack>

      {/* MODAL: ADD ZONE */}
      <Modal isOpen={isAddZoneOpen} onClose={onCloseAddZone} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Zone</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Zone Name</FormLabel>
              <Input
                placeholder="e.g. Balcony, VIP Terrace..."
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onCloseAddZone} size="sm">
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleAddZoneSubmit} size="sm">
              Create Zone
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* MODAL: EDIT CURRENT ZONE */}
      <EditZoneModal
        isOpen={isEditZoneOpen}
        onClose={onCloseEditZone}
        zone={currentZone}
        onSave={(data) => onEditZone(activeZoneIndex, data)}
      />

      {/* MODAL: ADD CATEGORY */}
      <Modal isOpen={isCatModalOpen} onClose={onCloseCatModal} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Seat Category</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>Category Name</FormLabel>
                <Input
                  placeholder="e.g. VIP, Golden, Regular..."
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Color</FormLabel>
                <ColorPickerPopover
                  value={newCatColor}
                  onChange={setNewCatColor}
                  size="sm"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onCloseCatModal} size="sm">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleAddCategorySubmit}
              size="sm"
            >
              Save Category
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* MODAL: EDIT CATEGORY */}
      {editingCatIndex !== null && (
        <Modal isOpen={true} onClose={() => setEditingCatIndex(null)} size="sm">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Category</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Category Name</FormLabel>
                  <Input
                    value={editCatName}
                    onChange={(e) => setEditCatName(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Color</FormLabel>
                  <ColorPickerPopover
                    value={editCatColor}
                    onChange={setEditCatColor}
                    size="sm"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="red"
                variant="ghost"
                mr="auto"
                size="sm"
                onClick={() => {
                  onDeleteCategory(editingCatIndex);
                  setEditingCatIndex(null);
                }}
              >
                Delete
              </Button>
              <Button
                variant="ghost"
                mr={2}
                onClick={() => setEditingCatIndex(null)}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                size="sm"
                onClick={() => {
                  onUpdateCategory(editingCatIndex, {
                    ...categories[editingCatIndex],
                    name: editCatName.trim(),
                    color: editCatColor,
                  });
                  setEditingCatIndex(null);
                }}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
